import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage } from "@/lib/api/_client";
import { useClonePdfTemplate } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import type { PdfTemplateListItem } from "@/lib/types/pdf-template";
import { T } from "./clone-pdf-template-dialog.i18n";

type FlowOption = { id: string; name: string; language?: string };

type ClonePdfTemplateDialogProps = {
  open: boolean;
  template: PdfTemplateListItem | null;
  /** Flows that have no PDF template yet; a flow takes at most one. */
  availableFlows: FlowOption[];
  onClose: () => void;
};

/**
 * QL-334 — copy a PDF template onto another flow. The copy takes that flow's
 * language; when it differs from the template's, the texts can be translated.
 */
export function ClonePdfTemplateDialog({
  open,
  template,
  availableFlows,
  onClose,
}: ClonePdfTemplateDialogProps) {
  const navigate = useNavigate();
  const t = useT(T);
  const cloneTemplate = useClonePdfTemplate();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    flowId: "",
    translate: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      // No flow is picked for the user: submitting writes onto it, so the
      // choice stays deliberate. Same-language flows are listed first instead.
      setFormData({
        name: `${template.name} (copy)`,
        description: template.description || "",
        flowId: "",
        translate: true,
      });
    }
    setError(null);
  }, [template]);

  const flowOptions = template
    ? [
        ...availableFlows.filter((f) => f.language === template.language),
        ...availableFlows.filter((f) => f.language !== template.language),
      ]
    : availableFlows;

  const targetFlow = availableFlows.find((f) => f.id === formData.flowId);
  const languageName = (code: string | undefined) =>
    code ? LOCALE_NAMES[code as LocaleCode] || code : "";
  const languageChanged =
    !!template && !!targetFlow?.language && targetFlow.language !== template.language;

  // Closing mid-request (a large plain copy takes a moment) would hide the result:
  // the navigation to the copy and any error land on a dismissed dialog.
  const blockDismissWhilePending = (event: { preventDefault: () => void }) => {
    if (cloneTemplate.isPending) event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;
    if (!formData.flowId) {
      setError(t.pleaseSelectFlow);
      return;
    }
    setError(null);

    try {
      const data = await cloneTemplate.mutateAsync({
        id: template.id,
        name: formData.name,
        description: formData.description || undefined,
        flowId: formData.flowId,
        translate: languageChanged ? formData.translate : undefined,
      });
      // QL-334 — a translated copy is made in the background; the list shows it.
      if (data.kind === "queued") {
        toast.info(t.copyQueued);
      } else {
        navigate(`/settings/pdf-templates/${data.id}`);
      }
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t.failedToClone));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !cloneTemplate.isPending) onClose();
      }}
    >
      <DialogContent
        onEscapeKeyDown={blockDismissWhilePending}
        onInteractOutside={blockDismissWhilePending}
      >
        <DialogHeader>
          <DialogTitle>{t.duplicateTemplate}</DialogTitle>
          <DialogDescription>{t.cloneDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="clone-pdf-name">
                {t.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clone-pdf-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clone-pdf-description">{t.description}</Label>
              <Textarea
                id="clone-pdf-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clone-pdf-flow">
                {t.targetFlow} <span className="text-destructive">*</span>
              </Label>
              {availableFlows.length > 0 ? (
                <Select
                  value={formData.flowId}
                  onValueChange={(flowId) => setFormData({ ...formData, flowId })}
                  disabled={cloneTemplate.isPending}
                >
                  <SelectTrigger id="clone-pdf-flow">
                    <SelectValue placeholder={t.selectFlow} />
                  </SelectTrigger>
                  <SelectContent>
                    {flowOptions.map((flow) => (
                      <SelectItem key={flow.id} value={flow.id}>
                        {flow.name}
                        {flow.language ? (
                          <span className="text-muted-foreground">
                            {" "}
                            · {languageName(flow.language)}
                          </span>
                        ) : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground">{t.noFreeFlows}</p>
              )}
              <p className="text-xs text-muted-foreground">{t.targetFlowHint}</p>
            </div>

            {languageChanged && (
              <div className="rounded-md border bg-muted/40 p-3 space-y-1.5">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="clone-pdf-translate"
                    checked={formData.translate}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, translate: checked === true })
                    }
                    disabled={cloneTemplate.isPending}
                  />
                  <label
                    htmlFor="clone-pdf-translate"
                    className="text-sm font-medium leading-none flex items-center gap-1.5"
                  >
                    <Languages className="h-3.5 w-3.5" />
                    {t.translateFromTo(
                      languageName(template?.language),
                      languageName(targetFlow?.language),
                    )}
                  </label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  {formData.translate ? t.translateHint : t.noTranslateHint}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={cloneTemplate.isPending}>{t.cancel}</DialogCancel>
            <Button
              type="submit"
              loading={cloneTemplate.isPending}
              disabled={availableFlows.length === 0}
            >
              {t.duplicate}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
